import { TextInput, type TextInputSize } from "@/shared/foundation/primitives";

import { isValueless, type NumericComparator } from "../FilterSchema";

import styles from "./valueInputs.module.css";

interface NumericValueInputProps {
  comparator: NumericComparator;
  value: number[];
  onChange: (next: number[]) => void;
  /** Optional unit of measure (e.g. "%", "ms"), shown inside the input's end
   *  so the entered value reads with its unit. Omitted/`null` shows none. */
  unit?: string | null;
  /**
   * Take focus on mount. The filter menu wants it; an editor that renders
   * several inputs at once does not. @default true
   */
  autoFocus?: boolean;
  /** Marks the input invalid, for an error shown beside it. */
  invalid?: boolean;
  /** Field size, on the `TextInput` scale. @default "md" */
  size?: TextInputSize;
}

export function NumericValueInput({
  comparator,
  value,
  onChange,
  unit = null,
  autoFocus = true,
  invalid = false,
  size = "md",
}: NumericValueInputProps) {
  const valueless = isValueless(comparator);

  if (valueless) {
    return null;
  }

  const text = value.length === 1 ? value[0].toString() : "";
  const input = (
    <TextInput
      type="number"
      autoFocus={autoFocus}
      aria-invalid={invalid || undefined}
      size={size}
      placeholder="Enter number..."
      value={text}
      className={unit ? styles.inputWithUnit : undefined}
      onChange={(event) => {
        const raw = event.target.value;
        if (raw === "") {
          onChange([]);
          return;
        }
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) {
          onChange([parsed]);
        }
      }}
    />
  );
  if (!unit) {
    return input;
  }
  return (
    <div className={styles.withUnit}>
      {input}
      <span className={styles.unit} aria-hidden="true">
        {unit}
      </span>
    </div>
  );
}
